import React, { useCallback, useEffect, useState, useMemo } from "react";
import ReusableModal from "../../../components/Modals/ReusableModal";
import "../css/bulk.css";
import DateSelector from "./DateSelector";
import {
  BULK_ACTIONS,
  WARNING_LABELS,
} from "../../../constants/config.constants";
import moment from "moment";
import { selectUser } from "../../../features/auth/authSlice";
import { useSelector } from "react-redux";
import {
  useBulkCopyMutation,
  useBulkDeleteMutation,
  useBulkLockAppointmentMutation,
  useBulkMoveMutation,
} from "../../../features/appointment/appointmentApiSlice";
import Loading from "../../../components/Loading";
import { getActionOptions, getSummaryText } from "../data";
import { Tooltip } from "@mui/material";

import { useFetchCancelReasonsMutation } from "../../../features/athena/athenaApiSlice";
const BulkActionsModal = React.memo(
  ({
    isOpen,
    onClose,
    setRecallSchedule,
    scheduleData,
    onMonthChange,
    count,
  }) => {
    const user = useSelector(selectUser);
    const [selectedAction, setSelectedAction] = useState(null);
    const [targetDate, setTargetDate] = useState(new Date());
    const [reason, setReason] = useState("");
    const [errorMessage, setErrorMessage] = useState(null);
    const [reasons, setReasons] = useState([]);

    const [deleteBulk, { isLoading: deleteLoading }] = useBulkDeleteMutation();
    const [moveBulk, { isLoading: moveLoading }] = useBulkMoveMutation();
    const [copyBulk, { isLoading: copyLoading }] = useBulkCopyMutation();
    const [lockBulk, { isLoading: lockLoading }] =
      useBulkLockAppointmentMutation();
    const [fetchCancelReasons, { isLoading: cancelReasonsLoading }] =
      useFetchCancelReasonsMutation();

    const isLoading = useMemo(
      () => deleteLoading || moveLoading || copyLoading || lockLoading,
      [deleteLoading, moveLoading, copyLoading, lockLoading]
    );

    useEffect(() => {
      if (isOpen) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        setTargetDate(tomorrow);
      }
    }, [isOpen]);

    React.useEffect(() => {
      if (user?.organisation?.practiceId) {
        fetchCancelReasons({ practiceId: user?.organisation?.practiceId })
          .unwrap()
          .then((res) => {
            setReasons(res.data);
            setReason(res.data?.find((r) => r.appointmentcancelreasonid === 28));
          });
      }
    }, []);

    const handleActionSelect = useCallback((action) => {
      setSelectedAction(action);
    }, []);

    const summaryText = useMemo(
      () => getSummaryText(scheduleData, selectedAction, targetDate),
      [scheduleData, selectedAction, targetDate]
    );

    const isDateRequired = useMemo(
      () =>
        selectedAction === BULK_ACTIONS.RESCHEDULE ||
        selectedAction === BULK_ACTIONS.COPY,
      [selectedAction]
    );

    const showWarning = useMemo(
      () =>
        selectedAction === BULK_ACTIONS.LOCK ||
        selectedAction === BULK_ACTIONS.CANCEL,
      [selectedAction]
    );

    const warningText = useMemo(
      () => WARNING_LABELS[selectedAction],
      [selectedAction]
    );

    const isLocked = useMemo(
      () =>
        scheduleData.appointments.some(
          (appointment) => appointment.status === "LOCKED"
        ),
      [scheduleData.appointments]
    );

    const handleDeleteCall = useCallback(
      async (date, providerId) => {
        try {
          setErrorMessage(null);
          await deleteBulk({
            data: {
              date,
              providerId,
              reason: reason?.name,
              reasonId: reason?.appointmentcancelreasonid,
              practiceId: user?.organisation?.practiceId,
            },
          }).unwrap();

          setRecallSchedule(true);
          onClose({});
        } catch (err) {
          setErrorMessage(err?.data?.message);
        }
      },
      [
        deleteBulk,
        reason,
        user?.organisation?.practiceId,
        setRecallSchedule,
        onClose,
      ]
    );

    const handleMoveCall = useCallback(
      async (date, providerId) => {
        try {
          setErrorMessage(null);
          await moveBulk({
            data: {
              currentDate: date,
              targetDate: moment(targetDate).format("YYYY-MM-DD"),
              providerId,
              practiceId: user?.organisation?.practiceId,
            },
          }).unwrap();

          setRecallSchedule(true);
          onClose({});
        } catch (err) {
          if (err?.data?.errors?.length) {
            setRecallSchedule(true);
            setErrorMessage(err?.data?.errors);
          } else {
            setErrorMessage([{ error: err?.message }]);
          }
        }
      },
      [
        moveBulk,
        targetDate,
        user?.organisation?.practiceId,
        setRecallSchedule,
        onClose,
      ]
    );

    const handleCopyCall = useCallback(
      async (date, providerId) => {
        try {
          setErrorMessage(null);
          await copyBulk({
            data: {
              currentDate: date,
              targetDate: moment(targetDate).format("YYYY-MM-DD"),
              providerId,
              practiceId: user?.organisation?.practiceId,
            },
          }).unwrap();

          setRecallSchedule(true);
          onClose({});
        } catch (err) {
          if (err?.data?.errors?.length) {
            setRecallSchedule(true);
            setErrorMessage(err?.data?.errors);
          } else {
            setErrorMessage([{ error: err?.message }]);
          }
        }
      },
      [
        copyBulk,
        targetDate,
        user?.organisation?.practiceId,
        setRecallSchedule,
        onClose,
      ]
    );

    const handleLockCall = useCallback(
      async (date, providerId, isLocked) => {
        try {
          setErrorMessage(null);
          await lockBulk({
            data: {
              currentDate: date,
              providerId,
              isLocked,
            },
          }).unwrap();

          setRecallSchedule(true);
          onClose({});
        } catch (err) {
          if (err?.data?.errors?.length) {
            setRecallSchedule(true);
            setErrorMessage(err?.data?.errors);
          } else {
            setErrorMessage([{ error: err?.message }]);
          }
        }
      },
      [lockBulk, setRecallSchedule, onClose]
    );

    const executeAction = useCallback(async () => {
      if (isDateRequired && !targetDate) {
        alert("Please select a target date");
        return;
      }

      const currentDate = scheduleData?.date;
      const currentProviderId = scheduleData?.providerId;

      const actionFunctions = {
        [BULK_ACTIONS.CANCEL]: () =>
          handleDeleteCall(currentDate, currentProviderId),
        [BULK_ACTIONS.RESCHEDULE]: () =>
          handleMoveCall(currentDate, currentProviderId),
        [BULK_ACTIONS.COPY]: () =>
          handleCopyCall(currentDate, currentProviderId),
        [BULK_ACTIONS.LOCK]: () =>
          handleLockCall(currentDate, currentProviderId, true),
        [BULK_ACTIONS.UNLOCK]: () =>
          handleLockCall(currentDate, currentProviderId, false),
      };

      const selectedActionFn = actionFunctions[selectedAction];

      if (!selectedActionFn) {
        console.warn(`Unknown action selected: ${selectedAction}`);
        return;
      }

      await selectedActionFn();
    }, [
      isDateRequired,
      targetDate,
      selectedAction,
      scheduleData?.date,
      scheduleData?.providerId,
      handleDeleteCall,
      handleMoveCall,
      handleCopyCall,
      handleLockCall,
    ]);

    const allow = {
      reschedule: user?.permissions?.includes('cc_home.bulk_reschedule'),
      copy: user?.permissions?.includes('cc_home.bulk_copy'),
      lock: user?.permissions?.includes('cc_home.bulk_lock'),
      unlock: user?.permissions?.includes('cc_home.bulk_lock'),
      cancel: user?.permissions?.includes('cc_home.bulk_cancel'),
    }

    const actionOptions = useMemo(() => getActionOptions(isLocked), [isLocked]);

    if (!isOpen) return null;

    return (
      <ReusableModal hideHeader open={isOpen} onClose={onClose}>
        {isLoading && <Loading loading={isLoading} />}
        <div className="max-w-[500px] md:max-w-[600px]">
          <div className="flex justify-between items-center border-b border-slate-200 mb-2 p-3">
            <div className="flex flex-col items-start">
              <h3 className="modal-title m-0 text-start">
                Bulk scheduling actions
              </h3>
              <p className="modal-subtitle m-0 text-start">
                Choose an action for{" "}
                {moment(scheduleData?.date).format("dddd, MMMM DD")}{" "}
                appointments
              </p>
            </div>
            <button
              className="close-btn-bulk p-2 border-none bg-transparent text-gray-500 hover:text-gray-700"
              onClick={onClose}
            >
              &times;
            </button>
          </div>

          <div>
            <div className="modal-body max-h-[calc(100vh-300px)] overflow-auto">
              <div className="action-selector">
                {actionOptions.map((action) => (
                  <Tooltip
                    key={action.id}
                    title={
                      action.id !== BULK_ACTIONS.UNLOCK && isLocked
                        ? "You are unable to take this action due to appointments in locked status"
                        : null
                    }
                    arrow
                  >
                    <button
                      className={`action-option ${
                        selectedAction === action.id ? "selected" : ""
                      } ${action.id === BULK_ACTIONS.CANCEL ? "cancel" : ""}`}
                      id={action.id}
                      onClick={() => handleActionSelect(action.id)}
                      disabled={action.id !== BULK_ACTIONS.UNLOCK && isLocked && !allow[action.id]}
                    >
                      <div className="action-icon">{action.icon}</div>
                      <div className="action-label">{action.label}</div>
                      <div className="action-description">
                        {action.description}
                      </div>
                    </button>
                  </Tooltip>
                ))}
              </div>

              {isDateRequired && (
                <DateSelector
                  selectedDate={targetDate}
                  onSelectDate={setTargetDate}
                  onMonthChange={onMonthChange}
                  count={count}
                />
              )}

              {selectedAction === BULK_ACTIONS.CANCEL && (
                <div className="flex flex-col items-start gap-3 px-3 md:flex-row md:items-center mb-3">
                  <label>Reason for cancellation: </label>
                  <select
                    value={reason?.name}
                    onChange={(e) =>
                      setReason(reasons.find((r) => r.name === e.target.value))
                    }
                    className="w-full p-2 border md:w-1/2"
                  >
                    <option value="">Reason for cancellation</option>
                    {reasons?.map((reason) => (
                      <option key={reason.name} value={reason.name}>
                        {reason.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {showWarning && selectedAction !== null && (
                <div className="warning-box" id="warningBox">
                  <div className="warning-title">
                    <span>⚠️</span>
                    <span>Important Notice</span>
                  </div>
                  <div className="warning-details text-wrap" id="warningText">
                    {warningText}
                  </div>
                </div>
              )}

              {selectedAction !== null && summaryText && (
                <div className="summary-box" id="actionSummary">
                  <div className="summary-title">Action Summary</div>
                  <div className="summary-details">
                    <strong>{summaryText.verb}</strong>{" "}
                    {summaryText?.count || 0} appointments{" "}
                    {summaryText.conjunction} {summaryText.sourceInfo}{" "}
                    {summaryText.conjunction2}
                    {summaryText.targetInfo ? (
                      <strong> {summaryText.targetInfo}</strong>
                    ) : null}
                    .
                    <br />
                    <span className="text-wrap">{summaryText.details}</span>
                  </div>
                </div>
              )}

              {errorMessage != null && (
                <p className="px-3 py-1 m-0 text-sm font-medium text-center text-red-400 border-b border-primarygrayborder">
                  <span className="m-0 font-bold">Error</span>: {errorMessage}
                </p>
              )}
            </div>

            <div className="modal-actions py-3 px-6">
              <button className="btn-bulk btn-bulk-cancel" onClick={onClose}>
                Cancel
              </button>
              <button
                className={`btn-bulk btn-bulk-primary ${summaryText?.count === 0 ? 'opacity-50 cursor-not-allowed cursor-not-allowed' : ''}`}
                id="confirmActionBtn"
                onClick={executeAction}
                disabled={summaryText?.count === 0}
              >
                <span className="icon">✓</span> Confirm Action
              </button>
            </div>
          </div>
        </div>
      </ReusableModal>
    );
  }
);

BulkActionsModal.displayName = "BulkActionsModal";

export default BulkActionsModal;
